import React, { FormEvent, ReactNode, FC, useState, useCallback, ReactElement } from 'react';
import { stringify } from 'query-string';
import { base64DecodeUrlSafe } from 'app/utils/string';
import { useCss } from 'app/utils/css';
import { ForgotPasswordRoute, SignupRoute, SendUnlockRoute } from 'app/constants/Routes';
import Link from 'app/components/presentation/link/Link';
import { useI18n } from 'app/components/localization';
import MarginGroup from 'app/components/layout/MarginGroup';
import Separator from 'app/components/layout/Separator';
import { useQuery } from 'app/components/Contexts';
import LoadingButtonA from 'app/components/presentation/buttons/LoadingButtonA';
import { ColorScheme } from 'app/components/presentation/buttons/ButtonClassNameA';
import FacebookButton from 'app/components/presentation/buttons/FacebookButton';
import ValidationError from 'app/components/presentation/forms/ValidationError';
import { InputField, PasswordField } from 'app/components/presentation/forms/Fields';
import {
  LoginConsumer,
  ILoginContext,
  LoginLoadingState,
} from 'app/components/auth/LoginContext';
import {
  AUTH_BUTTON_CONTAINER_STYLE,
  AUTH_ACTION_LINK,
  AUTH_SEPARATOR_TEXT,
} from './AuthStyles';
import OauthError, { isEmailTakenError } from 'app/services/oauth/OauthError';
import InputMarginGroup from 'app/components/auth/InputMarginGroup';
import FormSectionCard from 'app/components/presentation/containers/FormSectionCard';
import { DMMLoginState } from 'app/services/oauth/DMMAuthService';
import { FACEBOOK, DMM, PASSWORD } from 'app/constants/enums/AuthProvider';

const MARGIN_GROUP_MARGIN = '15px';

export const BaseLoginForm = React.memo<ILoginContext>(({
  allowSignup,
  dmmLoginEnv,
  dmmLoginState,
  emailTaken,
  error,
  errorAuthProvider,
  allowPasswordAuth,
  allowedExternalAuths,
  loadingState,
  loginWithCredentials,
  loginWithFacebook,
  loginWithDMM,
  registerWithDMM,
}) => {
  const { t } = useI18n();

  // When the user's password is insufficient, don't show the form and just display an error
  // message. The only means a user should have to regain access is to reset their password.
  if (
    errorAuthProvider === PASSWORD &&
    error &&
    error.cause instanceof OauthError &&
    error.cause.extendedCode === OauthError.ExtendedCodes.PASSWORD_INSUFFICIENT
  ) {
    return (
      <LoginBox allowSignup={allowSignup}>
        {t('auth.login-form.password-insufficient-message')}
      </LoginBox>
    );
  }

  const errorMessage = (loginType: ILoginContext['errorAuthProvider']) => {
    return loginType === errorAuthProvider ? <ErrorMessage error={error} /> : null;
  };

  const authChildren: ReactNode[] = [];

  if (allowPasswordAuth) {
    const defaultEmail = error && isEmailTakenError(error.cause) ?
      JSON.parse(base64DecodeUrlSafe(error.cause.oauthError.meta)).email : '';

    authChildren.push(
      <EmailAuthForm
        defaultEmail={defaultEmail}
        errorMessage={errorMessage(PASSWORD)}
        key="email-auth"
        isLoading={loadingState === LoginLoadingState.LOADING_CREDENTIALS}
        loginWithCredentials={loginWithCredentials}
      />,
    );
  }

  if (allowedExternalAuths.includes(FACEBOOK) && !emailTaken) {
    authChildren.push(
      <FacebookAuth
        errorMessage={errorMessage(FACEBOOK)}
        key="facebook-auth"
        isLoading={loadingState === LoginLoadingState.LOADING_FACEBOOK}
        loginWithFacebook={loginWithFacebook}
      />,
    );
  }

  if (allowedExternalAuths.includes(DMM) && !emailTaken) {
    const dmmEnvString = !!dmmLoginEnv && `(${dmmLoginEnv})`;

    const loginButton = (
      <LoadingButtonA
        isLoading={loadingState === LoginLoadingState.LOADING_DMM}
        onClick={loginWithDMM}
        fullWidth
      >
        {t('auth.login-form.signin-dmm')} {dmmEnvString}
      </LoadingButtonA>
    );

    const registerButton = (
      <LoadingButtonA
        onClick={registerWithDMM}
        fullWidth
        colorScheme={ColorScheme.Secondary}
        isLoading={loadingState === LoginLoadingState.LOADING_DMM_REGISTRATION}
      >
        {t('auth.login-form.signup-dmm')} {dmmEnvString}
      </LoadingButtonA>
    );

    let children: ReactNode;
    switch (dmmLoginState) {
      case DMMLoginState.HAS_REGISTERED_DMM_LOGIN:
        children = loginButton;
        break;
      case DMMLoginState.HAS_UNREGISTERED_DMM_LOGIN:
        children = registerButton;
        break;
      default:
        children = (
          <MarginGroup margin="10px">
            {loginButton}
            {registerButton}
          </MarginGroup>
        );
    }

    authChildren.push(
      <div data-testid="dmm-auth" key="dmm-auth">
        {errorMessage(DMM)}

        <MarginGroup margin="10px">
          {children}
        </MarginGroup>
      </div>,
    );
  }
  return (
    <LoginBox allowSignup={allowSignup}>
      <MarginGroup margin={MARGIN_GROUP_MARGIN}>
        {authChildren}
      </MarginGroup>
    </LoginBox>
  );
});

interface IErrorMessageProps {
  error: ILoginContext['error'];
}

const ErrorMessage: FC<IErrorMessageProps> = ({ error }) => {
  const { t } = useI18n();
  if (!error) {
    return null;
  }

  const errorMessage = error.message;
  const userLocked = (
    error.cause instanceof OauthError &&
    error.cause.extendedCode === OauthError.ExtendedCodes.USER_LOCKED
  );

  return (
    <MarginGroup>
      <ValidationError
        message={
          error.cause instanceof OauthError ?
          error.cause.localizedErrorMessage(t) || errorMessage :
          errorMessage
        }
      />
      {userLocked &&
        <Link to={SendUnlockRoute.getPathname()}>
          {t('auth.login-form.resend-unlock-instructions')}
        </Link>
      }
    </MarginGroup>
  );
};
interface IEmailAuthFormProps {
  defaultEmail?: string;
  errorMessage: ReactElement<IErrorMessageProps, typeof ErrorMessage> | null;
  isLoading: boolean;
  loginWithCredentials: ILoginContext['loginWithCredentials'];
}

const EmailAuthForm: FC<IEmailAuthFormProps> = ({
  defaultEmail = '',
  errorMessage,
  isLoading,
  loginWithCredentials,
}) => {
  const css = useCss();
  const { t } = useI18n();

  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      loginWithCredentials(email, password);
    },
    [email, loginWithCredentials, password],
  );

  return (
    <form data-testid="email-auth" onSubmit={onSubmit}>
      <InputMarginGroup>
        {errorMessage}

        <InputField
          label={t('auth.input.placeholder.email')}
          value={email}
          type="email"
          onChange={setEmail}
          required
          autoFocus
        />

        <PasswordField label={t('auth.input.placeholder.password')} value={password} onChange={setPassword} required />

        <div className={css('buttonContainer', AUTH_BUTTON_CONTAINER_STYLE)}>
          <LoadingButtonA isLoading={isLoading} type="submit">
            {t('auth.login-form.submit')}
          </LoadingButtonA>

          <div>
            <div className={css('forgotPasswordContainer', { marginLeft: '10px' })}>
              <Link
                to={ForgotPasswordRoute.getPathname()}
                className={css('forgotPasswordLink', {
                  ...AUTH_ACTION_LINK,
                  textAlign: 'right',
                  marginLeft: '5px',
                })}
              >
                {t('auth.login-form.forgot-password')}
              </Link>
            </div>
          </div>
        </div>
      </InputMarginGroup>
    </form>
  );
};

interface IFacebookAuthProps {
  errorMessage: ReactElement<IErrorMessageProps, typeof ErrorMessage> | null;
  isLoading: boolean;
  loginWithFacebook: ILoginContext['loginWithFacebook'];
}

const FacebookAuth: FC<IFacebookAuthProps> = ({ errorMessage, isLoading, loginWithFacebook }) => {
  const { t } = useI18n();

  return (
    <div data-testid="facebook-auth">
      <InputMarginGroup>
        <AuthOptionsSeparator />

        {errorMessage}

        <FacebookButton
          isLoading={isLoading}
          onClick={loginWithFacebook}
          fullWidth
          children={t('auth.login-form.signin-facebook')}
        />
      </InputMarginGroup>
    </div>
  );
};

interface ILoginBoxProps {
  allowSignup: boolean;
}

const LoginBox: FC<ILoginBoxProps> = ({ allowSignup, children }) => {
  const { t, tx } = useI18n();
  const query = useQuery();

  return (
    <FormSectionCard
      header={t('auth.login-form.title')}
      headerActionItem={
        allowSignup ?
          tx('auth.login-form.signup-link', {
            link: (
              <Link to={`${SignupRoute.getPathname()}?${stringify(query)}`}>
                {t('auth.login-form.signup')}
              </Link>
            ),
          })
          : ''
      }
    >
      {children}
    </FormSectionCard>
  );
};

export const AuthOptionsSeparator: React.SFC = () => {
  const css = useCss();
  const { t } = useI18n();

  return (
    <Separator>
      <span className={css('orSeparatorText', AUTH_SEPARATOR_TEXT)}>{t('auth.login-form.or-separator')}</span>
    </Separator>
  );
};

const LoginFormInjectDependencies: React.FC = () => (
  <LoginConsumer>{loginContext =>
    <BaseLoginForm {...loginContext} />
  }</LoginConsumer>
);

export default LoginFormInjectDependencies;
